import React, { useEffect, useState, useMemo } from 'react';
import {
	Box,
	Divider,
	IconButton,
	Typography,
	TextField,
	InputAdornment,
	Checkbox,
	Button,
	Tooltip
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import filterExpandedStyle from './FilterExpanded.style';
import {
	EDIT_FILTER_VALUE,
	SEARCH_FILTER_PLACEHOLDER,
	MAX_INT_FILTER_VALUE,
	SELECT_ALL_FILTER_VALUES,
	DELETE,
	TOOLTIP_ADD_NEW_FILTER_VAL,
	NO_MATCHING_RESULT,
	ADD_FILTER_VAL,
	CANNOT_ADD_FILTER_VAL,
	NO_FILTER_VALUES,
	GO_BACK_TO_FILTER,
	VALIDATION_ERROR_MSG,
	INVALID_VALUE,
	STR_TMPT_LIST,
	SSI_STORE_TRAIT_ATTRS
} from 'constants/constant';
import SearchIcon from '@material-ui/icons/Search';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { colors } from 'theme/color-palette';
import { deepClone } from 'helper/helper';
import { useSelector } from 'react-redux';
import { isCustomDataset } from 'helper/utils';
const { FONT_COLOR } = colors;

function FilterExpanded({ filterObj, handleShow, cbHandleAddChip, cbHandleChipDelete }) {
	const classes = filterExpandedStyle();

	const sbuDepartments = useSelector((state) => state.templateReducer.sbuDepartments);
	const templateId = useSelector((state) => state.templateReducer.selectedTemplate);

	const [selectedValue, setSelectedValue] = useState([]);
	const [localVal, setLocalVal] = useState(filterObj.val);
	const [searchedVal, setSearchedVal] = useState(localVal);
	const [searchText, setSearchText] = useState('');
	const [showValidationError, setShowValidationError] = useState(false);
	const [invalidNumber, setInvalidNumber] = useState(false);

	const isSBUField = useMemo(
		() => filterObj.val.length > 0 && filterObj.val[0].deptNum,
		[filterObj.val]
	);
	const isCustomDS = isCustomDataset(templateId);

	const maxValuesAllowed =
		(STR_TMPT_LIST.includes(Number(templateId)) || isCustomDS) &&
		['Trait Number'].includes(filterObj.busName)
			? SSI_STORE_TRAIT_ATTRS.MAX_FILTER_VALUE
			: MAX_INT_FILTER_VALUE;

	const selectAll = (e) => {
		if (e.currentTarget.checked) {
			if (isSBUField) {
				setSelectedValue(searchedVal.map((item) => item.deptNum));
			} else {
				setSelectedValue(searchedVal);
			}
		} else {
			setSelectedValue([]);
		}
	};

	const updateSelectedValue = (e) => {
		const copyOfSelectedVal = deepClone(selectedValue);
		if (e.currentTarget.checked) {
			copyOfSelectedVal.push(e.currentTarget.name);
		} else {
			const index = copyOfSelectedVal.indexOf(e.currentTarget.name);
			copyOfSelectedVal.splice(index, 1);
		}
		setSelectedValue(copyOfSelectedVal);
	};

	const handleDeleteValues = () => {
		let copyOfValues = localVal.filter((item) =>
			item.deptNum ? !selectedValue.includes(item.deptNum) : !selectedValue.includes(item)
		);
		const copyOfSearchedValues = searchedVal.filter((item) =>
			item.deptNum ? !selectedValue.includes(item.deptNum) : !selectedValue.includes(item)
		);
		setLocalVal(copyOfValues);
		setSearchedVal(copyOfSearchedValues);
		if (isSBUField) {
			copyOfValues = copyOfValues.map((item) => item.deptNum);
		}
		setSelectedValue([]);
		cbHandleChipDelete(copyOfValues, filterObj.index);
	};

	const handleAddValue = () => {
		if (localVal.length < maxValuesAllowed) {
			let newList = deepClone(localVal);
			if (isSBUField) {
				const obj = sbuDepartments.filter(
					(item) => item.deptNum.includes(searchText) || item.deptDesc.includes(searchText)
				)[0];
				newList.push(obj);
				setLocalVal(newList);
				setSearchedVal([obj]);
				newList = newList.map((item) => item.deptNum);
				cbHandleAddChip(undefined, filterObj.index, newList);
			} else {
				newList.push(searchText);
				setLocalVal(newList);
				setSearchedVal([searchText]);
				cbHandleAddChip(undefined, filterObj.index, newList);
			}
		}
	};

	const handleSearch = (e) => {
		const searchText = e.currentTarget.value;
		if (isNaN(Number(searchText))) {
			setShowValidationError(true);
			e.preventDefault();
			return;
		}
		if (isSBUField) {
			const obj = sbuDepartments.filter((item) => item.deptNum.includes(searchText));
			if (obj.length === 0) {
				setInvalidNumber(true);
			} else {
				setInvalidNumber(false);
			}
		}
		setShowValidationError(false);
		setSearchText(e.currentTarget.value);
	};

	useEffect(() => {
		if (searchText === '') {
			setSearchedVal(localVal);
		} else {
			const copyOfSelectedVal = localVal.filter((item) => {
				if (item.deptNum) {
					return (
						item.deptNum.includes(searchText) ||
						item.deptDesc.toLowerCase().includes(searchText.toLowerCase())
					);
				}
				return item.includes(searchText);
			});
			setSearchedVal(copyOfSelectedVal);
		}
	}, [searchText]);

	const isCheckedItem = (item) => {
		return selectedValue.includes(item);
	};

	const getTextForNoMatchingResult = () => {
		if (localVal.length < maxValuesAllowed) {
			if (invalidNumber) {
				return (
					<>
						<Typography className={classes.noMatchFoundText} style={{ marginTop: '16px' }}>
							&#39;{searchText}&#39;
						</Typography>
						<Typography className={classes.noMatchFoundText}>{INVALID_VALUE}</Typography>
					</>
				);
			}
			return (
				<Button className={classes.addBtn} onClick={handleAddValue}>
					{ADD_FILTER_VAL} &#39;{searchText}&#39;
				</Button>
			);
		}
		return (
			<Typography className={classes.noMatchFoundText}>
				{CANNOT_ADD_FILTER_VAL} &#39;{searchText}&#39;
			</Typography>
		);
	};

	const getNoMatchContent = () => {
		if (searchText.length > 0) {
			return (
				<>
					<Typography className={classes.noMatchFoundText}>{NO_MATCHING_RESULT}</Typography>
					{getTextForNoMatchingResult()}
				</>
			);
		} else {
			return (
				<>
					<Typography className={classes.noMatchFoundText}>{NO_FILTER_VALUES}</Typography>
					<Button className={classes.addBtn} onClick={() => handleShow()}>
						{GO_BACK_TO_FILTER}
					</Button>
				</>
			);
		}
	};

	return (
		<Box className={classes.relative}>
			<Box style={{ marginBottom: '24px' }}>
				<Box
					aria-controls="expanded-filter-header"
					className={classes.titleContainer}
					id="expanded-filter-header">
					<Typography className={classes.titleFont}>
						<IconButton
							size="small"
							aria-label="Close"
							className={classes.backIcon}
							onClick={() => handleShow()}>
							<ArrowBackIcon />
						</IconButton>
						<span style={{ marginLeft: '42px' }}>{EDIT_FILTER_VALUE}</span>
					</Typography>
					<Divider />
					<Typography className={classes.subtitleFont}>
						<span style={{ marginLeft: '16px' }}>
							{filterObj.busName}: {filterObj.condition}
						</span>
					</Typography>
					<Divider />
				</Box>
				<Box className={classes.searchBox}>
					<TextField
						placeholder={SEARCH_FILTER_PLACEHOLDER}
						value={searchText}
						id="search-icon"
						className={classes.searchBox}
						onChange={(e) => handleSearch(e)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end" style={{ marginRight: '16px' }}>
									<IconButton onClick={() => setSearchText('')}>
										<CloseIcon />
									</IconButton>
								</InputAdornment>
							)
						}}
					/>
					{showValidationError && (
						<p className={classes.errorMsgBox}>
							<InfoOutlinedIcon /> {VALIDATION_ERROR_MSG}
						</p>
					)}
					<Divider style={{ width: '100%', marginTop: '100px' }} />
				</Box>
				<Box className={classes.filterValueOptionBox}>
					<span className={classes.checkBoxLabel}>
						<Checkbox
							id="select-all"
							onChange={(e) => selectAll(e)}
							color="primary"
							name="selectAll"
							className={classes.checkbox}
							style={{ marginLeft: 0 }}
						/>
						<p style={{ marginLeft: '6px' }}>
							{`${SELECT_ALL_FILTER_VALUES} (${searchedVal.length}/${maxValuesAllowed})`}
						</p>
						<Divider
							orientation="vertical"
							style={{ height: '16px', margin: '16px', marginRight: '0px' }}
						/>
						<Button
							className={classes.deleteBtn}
							disabled={selectedValue.length === 0}
							onClick={handleDeleteValues}>
							{DELETE}
						</Button>
						<Tooltip
							classes={{ tooltip: classes.customWidth }}
							title={TOOLTIP_ADD_NEW_FILTER_VAL}
							placement="bottom">
							<InfoOutlinedIcon className={classes.infoIcon} />
						</Tooltip>
					</span>
				</Box>
				{searchedVal.length > 0 ? (
					<Box className={classes.filterValuesBox}>
						{searchedVal.map((item) => (
							<span className={classes.valueCheckBoxLabel} key={item.deptNum ? item.deptNum : item}>
								<Checkbox
									checked={isCheckedItem(item.deptNum ? item.deptNum : item)}
									name={item.deptNum ? item.deptNum : item}
									color={FONT_COLOR}
									className={classes.checkbox}
									onChange={(e) => updateSelectedValue(e)}
									style={{ marginLeft: 0 }}
								/>
								<p style={{ margin: '0 6px', color: { FONT_COLOR } }}>
									{item.deptNum ? `${item.deptNum} - ${item.deptDesc}` : item}
								</p>
							</span>
						))}
					</Box>
				) : (
					<Box className={classes.noMatchFoundBox}>{getNoMatchContent()}</Box>
				)}
			</Box>
		</Box>
	);
}

export default FilterExpanded;
